import React, { useEffect, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/job-management.css";
import { createPerson } from "../services/peopleService";
import { getCompanyNames } from "../services/companyService";
import { getJobsByCompanyId } from "../services/jobService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";

const initialForm = {
  name: "",
  surname: "",
  email: "",
  phone: "",
  is_manager: false,
  company_id: "",
  job_id: "",
};

const PeopleCreatePage = () => {
  const { setActiveSection } = useAppContext();
  const [form, setForm] = useState(initialForm);
  const [companies, setCompanies] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  // Helper to load jobs for a specific company
  const loadJobsForCompany = async (companyId) => {
    if (!companyId) {
      setFilteredJobs([]);
      return;
    }
    try {
      const jobsRes = await getJobsByCompanyId(companyId).catch(() => []);
      setFilteredJobs(Array.isArray(jobsRes) ? jobsRes : []);
    } catch {
      setFilteredJobs([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const companiesRes = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);
      } catch (e) {
        // ignore, page can still render
      }
    })();
  }, []);

  // Load jobs when company changes
  useEffect(() => {
    if (form.company_id) {
      loadJobsForCompany(form.company_id);
    } else {
      setFilteredJobs([]);
      setForm((prev) => ({ ...prev, job_id: "" }));
    }
  }, [form.company_id]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "radio" && name === "is_manager") {
      setForm((prev) => ({ ...prev, is_manager: value === "true" }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const onCancel = () => setActiveSection("people-management");

  const onSubmit = async (e) => {
    e.preventDefault();
    // Validation checks
    const name = String(form.name || "").trim();
    const surname = String(form.surname || "").trim();
    const email = String(form.email || "").trim();
    const phone = String(form.phone || "").trim();

    // Required field validations
    if (!name) {
      toast.error("First Name is required");
      return;
    }

    if (!surname) {
      toast.error("Surname is required");
      return;
    }

    if (!email) {
      toast.error("Email is required");
      return;
    }

    if (!phone) {
      toast.error("Phone is required");
      return;
    }

    if (!form.company_id) {
      toast.error("Company is required");
      return;
    }

    if (!form.job_id) {
      toast.error("Job is required");
      return;
    }

    // Name validations (no numbers or special characters except spaces, hyphens, apostrophes)
    const nameRegex = /^[A-Za-z\s'-]+$/;
    if (!nameRegex.test(name)) {
      toast.error(
        "First Name can only contain letters, spaces, hyphens, and apostrophes",
      );
      return;
    }

    if (!nameRegex.test(surname)) {
      toast.error(
        "Surname can only contain letters, spaces, hyphens, and apostrophes",
      );
      return;
    }

    // Name length validations
    if (name.length < 2) {
      toast.error("First Name must be at least 2 characters long");
      return;
    }

    if (name.length > 50) {
      toast.error("First Name must not exceed 50 characters");
      return;
    }

    if (surname.length < 2) {
      toast.error("Surname must be at least 2 characters long");
      return;
    }

    if (surname.length > 50) {
      toast.error("Surname must not exceed 50 characters");
      return;
    }

    // Email validation (comprehensive)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (email.length > 100) {
      toast.error("Email must not exceed 100 characters");
      return;
    }

    // Phone validation (international format with optional +, digits, spaces, hyphens, parentheses)
    const phoneRegex = /^[\d\s\-+()]{7,20}$/;
    if (!phoneRegex.test(phone)) {
      toast.error(
        "Please enter a valid phone number (7-20 characters, can include +, -, (), and spaces)",
      );
      return;
    }

    // Check if phone contains at least 7 digits
    const digitCount = phone.replace(/\D/g, "").length;
    if (digitCount < 7) {
      toast.error("Phone number must contain at least 7 digits");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name,
        surname,
        email: email.toLowerCase(), // Normalize email to lowercase
        phone,
        is_manager: !!form.is_manager,
        company_id: Number(form.company_id),
        job_id: Number(form.job_id),
      };
      await createPerson(payload);
      toast.success(
        `Person "${name} ${surname}" has been created successfully!`,
      );
      setActiveSection("people-management");
    } catch (e) {
      toast.error(e?.message || "Failed to create person");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Add Person</h2>
            <p className="page-subtitle">Create a new person.</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {" "}
        <div className="role-card">
          <form onSubmit={onSubmit}>
            <div
              className="form-grid"
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              <div className="field">
                <label>
                  First Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="John"
                  required
                  minLength={2}
                  maxLength={50}
                  pattern="[A-Za-z\s'-]+"
                  title="Only letters, spaces, hyphens, and apostrophes allowed"
                />
              </div>
              <div className="field">
                <label>
                  Surname <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  name="surname"
                  value={form.surname}
                  onChange={onChange}
                  placeholder="Doe"
                  required
                  minLength={2}
                  maxLength={50}
                  pattern="[A-Za-z\s'-]+"
                  title="Only letters, spaces, hyphens, and apostrophes allowed"
                />
              </div>
              <div className="field">
                <label>
                  Email <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="john@example.com"
                  required
                  maxLength={100}
                />
              </div>
              <div className="field">
                <label>
                  Phone <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+1234567890"
                  required
                  pattern="[\d\s\-+()]{7,20}"
                  title="Enter a valid phone number (7-20 characters)"
                />
              </div>
              <div className="field">
                <label>
                  Company <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  name="company_id"
                  value={form.company_id}
                  onChange={onChange}
                  required
                >
                  <option value="">Select company</option>
                  {companies.map((c) => (
                    <option
                      key={c.company_id || c.id}
                      value={c.company_id || c.id}
                    >
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>
                  Job <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  name="job_id"
                  value={form.job_id}
                  onChange={onChange}
                  required
                  disabled={!form.company_id}
                >
                  <option value="">
                    {!form.company_id ? "Select company first" : "Select job"}
                  </option>
                  {filteredJobs.map((j) => (
                    <option key={j.job_id} value={j.job_id}>
                      {j.name}
                    </option>
                  ))}
                </select>
                {form.company_id && filteredJobs.length === 0 && (
                  <small
                    style={{
                      color: "#f59e0b",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    No jobs available for selected company
                  </small>
                )}
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Is Manager <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    marginTop: 6,
                  }}
                >
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="is_manager"
                      value="true"
                      checked={form.is_manager === true}
                      onChange={onChange}
                      required
                    />
                    Yes
                  </label>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="is_manager"
                      value="false"
                      checked={form.is_manager === false}
                      onChange={onChange}
                      required
                    />
                    No
                  </label>
                </div>
              </div>
            </div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                style={{ marginLeft: 8 }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Person"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PeopleCreatePage;
